const _ = require("lodash");
const moment = require("moment");
const LoadState = require("./util/LoadState");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const BookstoreUtil = require("../../../shared/BookstoreUtil");
const MiscBlockUtil = require("../../../shared/MiscBlockUtil");
const BatexAloud = require("../canvasmath/BatexAloud");
const Bundles = require("../Bundles");
const TrainerCore = require("../../../shared/TrainerCore");

// Block shape {
//   blockID,
//   model,
//   load,
//   loadError,
//   currentResult: Result structure, see OpsResult,
//   trophy: {
//     load,
//     loadError,
//     trophyResult: Result structure, see OpsResult,
//   },
//   unlocksAt: rarely set field if block fails to load due to being locked but
//     frontend gives an unlock time, only used for book tests ATM,
// }

const init = function(blockID, blockType) {
	const base = {
		blockID,
		model: null,
		currentResult: null,
		history: LoadState.loadInit({}),
	};
	if (blockType !== "lab") {
		base.trophy = LoadState.loadInit({trophyResult: null});
	}
	return LoadState.loadInit(base);
};

const clearResult = function(block, resultSourceType) {
	if (resultSourceType === "block") {
		block = LoadState.loadRestart(block);
		block = extendIn(block, [], {currentResult: null});
		block = updatePercentCompletion(block);
	} else if (resultSourceType === "trophy") {
		block = setIn(block, ["trophy"], x => LoadState.loadRestart(x));
		block = extendIn(block, ["trophy"], {trophyResult: null});
	}
	return block;
};

const clearPercentCompleteOnDemoResult = function(block, resultSourceType) {
	if (resultSourceType === "block") {
		block = setIn(block, ["model", "recentHistory", 0, "percentComplete"], 0);
		block = setIn(block, ["model", "percentComplete"], 0);
	}
	return block;
};

const updatePercentCompletion = function(block, percentComplete, unfinished) {
	block = extendIn(block, "model", {
		unfinished: !!unfinished,
		percentComplete: percentComplete || 0,
	});
	const blockType = _.get(block, "model.blockType");
	const isLabOrMisc = blockType === "lab" || blockType === "misc";
	if (percentComplete && !isLabOrMisc) {
		const path = [
			"model",
			"recentHistory",
			block.model.recentHistory.length - 1,
			"percentComplete",
		];
		block = setIn(block, path, percentComplete);
	}
	return block;
};

const isUnlocked = block => block.model && block.model.unlocked;

//History is available if a block has been completed once and is not challenge
const canSeeHistory = function(block) {
	const m = (block && block.model) || {};
	return m && m.timesCompleted && m.blockType !== "challenge";
};

const shouldCheckAboutDeletingLimitedResult = function(block) {
	const m = (block && block.model) || {};
	return (
		m &&
		m.starsAvailable === 3 &&
		m.limitedInProgressResult &&
		!block.currentResult
	);
};

const warnAboutMaxStarsBeforeEntering = function(block) {
	const m = (block && block.model) || {};
	return (
		m &&
		m.starsAvailable < 3 &&
		m.starsObtained >= 1 &&
		!m.unfinished &&
		!block.currentResult
	);
};

const isThrottled = function(block) {
	const m = (block && block.model) || {};

	const timesCompleted = m.timesCompleted || 0;
	return (
		timesCompleted > TrainerCore.MAX_COMPLETIONS_BEFORE_XP_LIMITED ||
		(m.starsObtained === 3 &&
			timesCompleted > TrainerCore.MAX_COMPLETIONS_ON_3_STAR_BEFORE_XP_LIMITED)
	);
};

const calculateMaxXP = function(block) {
	const m = (block && block.model) || {};
	return (
		TrainerCore.MAX_TRIALS_XP + (m.starsObtained || 0) * TrainerCore.STAR_XP
	);
};

const warnAboutMaxStarsAfterEntering = function(block) {
	const m = (block && block.model) || {};
	return m && m.starsAvailable < 3 && m.starsObtained === 0 && !m.unfinished;
};

// Returns [grade, chapterNumber]. Usually just the block's chapter, but also
// tries to return something valid for chapterless blocks like book tests.
const getClosestGradeChapter = function(block) {
	const m = (block && block.model) || {};
	const bookHash = getBookTestHash(block);
	if (bookHash) {
		return BookstoreUtil.getBookLastChapter(bookHash);
	}
	return [m.gradeNumber, m.chapterIndex + 1];
};

const getBookSectionID = function(block) {
	const m = (block && block.model) || {};
	return m.bookSectionID;
};

//Takes in a block and a moment of the vault's current time
const haveLimitedMaxStarsExpired = function(block, nowMoment) {
	const m = (block && block.model) || {};
	return (
		m &&
		m.limitedStarsExpiration &&
		moment(m.limitedStarsExpiration).isSameOrBefore(nowMoment)
	);
};

const isCoreLesson = function(block) {
	const m = (block && block.model) || {};
	return m.requirementLevel === "core" || m.requirementLevel === "mastery";
};

const isEnrichmentLesson = function(block) {
	const m = (block && block.model) || {};
	return m.requirementLevel === "enrichment" || m.requirementLevel === "bonus";
};

const isAnyTest = function(block) {
	return isChapterTest(block) || isBookTest(block);
};

const isChapterTest = function(block) {
	const m = (block && block.model) || {};
	return m.blockType === "test";
};

const isBookTest = function(block) {
	return !!getBookTestHash(block);
};

const {getBookTestHashFromName} = MiscBlockUtil;

const getBookTestHash = function(block) {
	return getBookTestHashFromName(_.get(block, "model.miscBlockName"));
};

const isTrophyComplete = function(block) {
	const m = (block && block.model) || {};
	return m.isTrophyComplete;
};

//Takes in a block and a moment of the vault's current time
const isInTestCooldown = function(block, nowMoment) {
	const m = (block && block.model) || {};
	return !!(
		m &&
		m.testCooldownExpiration &&
		moment(nowMoment).isBefore(m.testCooldownExpiration)
	);
};

const suppressUnlockFromProgress = function(block) {
	const m = (block && block.model) || {};
	return (
		m.lockOverride === "locked" ||
		(isInTestCooldown(block, moment()) && m.lockOverride !== "unlocked")
	);
};

const hasVideoSetID = function(blockModel) {
	if (!blockModel) return false;
	const blockType = blockModel.type;
	const isTest = blockType === "test" || blockType === "bookTest";
	return !isTest && blockModel.videoSetID;
};

const getReadAloudTextInfo = function(user, block, result, problem) {
	const InstructionsUtil = Bundles.requireOpt("trainer", "InstructionsUtil");
	let text = "";

	if (InstructionsUtil) {
		const instructionsViewData = InstructionsUtil.getInstructionsViewData({
			vaultState: {},
			user,
			block,
			result,
			problem,
			problemIndex: result.currentProblemIndex,
		});
		text = BatexAloud.readAloudBatexText({
			batex: instructionsViewData?.instructionsText,
			customEnvironments: {
				example: {
					measure: () => {},
					draw: () => {},
					configCommands: [],
					isInline: true,
				},
			},
		});
	}

	return {
		text,
		type: "instructions",
	};
};

module.exports = {
	init,
	clearResult,
	clearPercentCompleteOnDemoResult,
	updatePercentCompletion,
	isThrottled,
	calculateMaxXP,
	isUnlocked,
	canSeeHistory,
	warnAboutMaxStarsBeforeEntering,
	warnAboutMaxStarsAfterEntering,
	shouldCheckAboutDeletingLimitedResult,
	haveLimitedMaxStarsExpired,
	getClosestGradeChapter,
	getBookSectionID,
	isCoreLesson,
	isEnrichmentLesson,
	isAnyTest,
	isChapterTest,
	isBookTest,
	getBookTestHashFromName,
	getBookTestHash,
	isTrophyComplete,
	isInTestCooldown,
	suppressUnlockFromProgress,
	hasVideoSetID,
	getReadAloudTextInfo,
	isBlockComplete: MiscBlockUtil.isBlockComplete,
};
